import {factories} from '@strapi/strapi';
import {BotInfo} from '../../bot/koni-bot';
import {createAuditLog} from "../utils/audit-log";
import {DeploymentType} from "../../types";
import {KoniBotServiceType} from "../bot/koni-bot";
import {DOCUMENT_BATCH_SIZE} from "../../../../constants";

const UID = 'plugin::telegram-bot.telegram-bot';

const TelegramBotService = factories.createCoreService(UID, ({strapi}) => ({
  koniBot: strapi.plugin('telegram-bot').service<KoniBotServiceType>('koniBot'),
  botCache: {},
  async getBotByUsername(username: string) {
    if (!this.botCache[username]) {
    // Get bot from DB
      const rs = await strapi.documents(UID).findMany({
          limit: 1,
          filters:{
            username:{
              $eq: username,
            },
          },
        });

      if (rs[0]) {
        this.botCache[username] = rs[0];
      }
    }
    return this.botCache[username]
  },
  async autoCreateBot() {
    // Auto create & sync bot with bot configs
    const botMap = this.koniBot.botMap;
    const existedBots = await strapi.db.query(UID).findMany();

    const existedBotMap = existedBots.reduce((acc: Record<string, any>, bot: any) => {
      acc[bot.name] = bot;
      return acc;
    }, {});

    const botDoc = strapi.db.query(UID);
    const metadata = {
      rateLimit: 25,
      intervalTime: 1000,
    }
    // Compare existed bot with botMap and create new bot if not existed
    for (const [key, botHandler] of Object.entries(botMap)) {
      if (!existedBotMap[key]) {
        const newBot = await botDoc.create({
          data: {
            name: botHandler.botInfo?.username || key,
            username: key,
            token: botHandler.token,
            metadata,
          }
        });

        botHandler.setBotModel(newBot);
      } else {
        // Update token if token is changed
        if (existedBotMap[key]) {
          const existedBot = existedBotMap[key];
          await botDoc.update({
            where: {
              id: existedBot.id,
            },
            data: {
              name: botHandler.botInfo?.username || key,
              token: botHandler.token,
              metadata,
            }
          });

          botHandler.setBotModel(existedBotMap[key]);
        }
      }
    }
  },

  async _sendMessage({telegramUsers, botData, data, actionType}: {telegramUsers: any, botData: any, data: any, actionType: any}){
    const sentMap: Record<number, number> = {};
    const usersList = [];
    telegramUsers.forEach((user) => {
      const telegramId = user.telegram_id;

      if (!telegramId || sentMap[telegramId]) {
        return;
      }
      sentMap[telegramId] = 1;
      usersList.push({
        telegram_id: user.telegram_id,
        username: user.telegram_username,
        documentId: user.documentId,
      })
    });

    const username = botData.username;
    const metaData = botData.metadata;
    const rateLimit = metaData?.rateLimit || 20;
    const intervalTime = metaData?.intervalTime || 1000;

    const botInfo: BotInfo = {
      name: username,
      rateLimit,
      intervalTime,
    };

    let bot = null;
    try {
      bot = this.koniBot.getBotHandler(username);
      if (!bot) {
        return;
      }
    } catch (e) {
      strapi.log.error(e);
      return;
    }

    // Send message to all users
    usersList.forEach((user: any) => {
      bot.addMessageQueue('sendPhoto', botInfo, {chat_id: user.telegram_id, ...data});
    });

    // Create audit log
    const auditLog = {users: usersList, data};
    const singularName = 'plugin::telegram-bot.telegram-notification';
    const model = 'telegram-notification';
    await createAuditLog(singularName, model, auditLog, actionType);
  },

  async sendMessage(botID: string, data: any, users = [], actionType: DeploymentType = DeploymentType.NOTIFICATION_PRODUCTION) {
    const botData = await strapi.documents('plugin::telegram-bot.telegram-bot').findOne({
      documentId: botID,
    });

    if (!botData) {
      return;
    }

    // Get all users if users are not provided
    if (users.length === 0) {
      // Issue-78 | Get all users linking with this bot
      const filter = {
        bot_list: {
          documentId: botID
        }
      };
      const totalUser = await strapi.documents('plugin::telegram-bot.telegram-user').count({
        filters:filter
      });
      const actualBatchSize = totalUser < DOCUMENT_BATCH_SIZE ? totalUser : DOCUMENT_BATCH_SIZE;
      const totalBatches = Math.ceil(totalUser / actualBatchSize);

      for (let batch = 0; batch < totalBatches; batch++) {
        const start = batch * actualBatchSize;
        const batchUser = await strapi.documents('plugin::telegram-bot.telegram-user').findMany({
          limit: actualBatchSize,
          start: start,
          filters:filter
        });

        // Send message
        await this._sendMessage({
          telegramUsers:batchUser,
          botData: botData,
          data,
          actionType
        })
      }
    } else {
      // Send message
      await this._sendMessage({
        telegramUsers:users,
        botData: botData,
        data,
        actionType
      })
    }
  },
}));

export type TelegramBotServiceType = ReturnType<typeof TelegramBotService>;

export default TelegramBotService;
